import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import moment from "moment";

import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { SUGGESTION_CATEGORIES } from "../../components/SuggestionModal";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

export const SUGGESTION_STATUSES = [
  { value: "new", label: "Nova", color: "#1a73d7" },
  { value: "analyzing", label: "Em análise", color: "#f29900" },
  { value: "planned", label: "Planejada", color: "#8e44ad" },
  { value: "done", label: "Implementada", color: "#1e8e3e" },
  { value: "rejected", label: "Recusada", color: "#9aa0a6" },
];

const categoryLabel = (value) =>
  SUGGESTION_CATEGORIES.find((c) => c.value === value)?.label || value;

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.padding,
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  title: {
    maxWidth: 320,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  statusSelect: {
    minWidth: 140,
    fontSize: "0.85rem",
  },
  detailText: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    marginBottom: theme.spacing(2),
  },
  screenshot: {
    maxWidth: "100%",
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(1),
  },
}));

const StatusSelect = ({ value, onChange, className }) => (
  <Select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={className}
    disableUnderline
    renderValue={(v) => {
      const s = SUGGESTION_STATUSES.find((x) => x.value === v);
      return (
        <Chip
          size="small"
          label={s?.label || v}
          style={{ backgroundColor: s?.color, color: "#fff" }}
        />
      );
    }}
  >
    {SUGGESTION_STATUSES.map((s) => (
      <MenuItem key={s.value} value={s.value}>
        {s.label}
      </MenuItem>
    ))}
  </Select>
);

const Suggestions = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [records, setRecords] = useState([]);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [screenshotUrl, setScreenshotUrl] = useState(null);

  useEffect(() => {
    if (!user.super) {
      toast.error("Esta página é restrita ao administrador do sistema.");
      history.push("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setRecords([]);
    setPageNumber(1);
  }, [statusFilter]);

  useEffect(() => {
    if (!user.super) return;
    let cancelled = false;

    const fetchSuggestions = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/suggestions", {
          params: { pageNumber, status: statusFilter || undefined },
        });
        if (cancelled) return;
        setRecords((prev) =>
          pageNumber === 1 ? data.records : [...prev, ...data.records]
        );
        setCount(data.count);
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      }
      if (!cancelled) setLoading(false);
    };

    fetchSuggestions();
    return () => {
      cancelled = true;
    };
  }, [pageNumber, statusFilter, user.super]);

  // Libera o blob do print quando o detalhe é fechado.
  useEffect(() => {
    return () => {
      if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
    };
  }, [screenshotUrl]);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setPageNumber((prev) => prev + 1);
    }
  };

  const handleStatusChange = async (suggestion, status) => {
    try {
      const { data } = await api.put(`/suggestions/${suggestion.id}`, { status });
      const matchesFilter = !statusFilter || statusFilter === data.status;
      setRecords((prev) =>
        matchesFilter
          ? prev.map((r) => (r.id === data.id ? data : r))
          : prev.filter((r) => r.id !== data.id)
      );
      if (!matchesFilter) setCount((c) => c - 1);
      if (selected?.id === data.id) setSelected(data);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenDetail = async (suggestion) => {
    setSelected(suggestion);
    setScreenshotUrl(null);
    if (!suggestion.mediaPath) return;

    // O print fica numa rota autenticada (não em /public), então é baixado
    // via api com o token e exibido como blob.
    try {
      const { data } = await api.get(`/suggestions/${suggestion.id}/media`, {
        responseType: "blob",
      });
      setScreenshotUrl(URL.createObjectURL(data));
    } catch (err) {
      toastError(err);
    }
  };

  const handleCloseDetail = () => {
    setSelected(null);
    setScreenshotUrl(null);
  };

  return (
    <MainContainer>
      <Dialog open={!!selected} onClose={handleCloseDetail} maxWidth="md" fullWidth scroll="paper">
        {selected && (
          <>
            <DialogTitle>
              #{selected.id} · {selected.title}
            </DialogTitle>
            <DialogContent dividers>
              <Typography className={classes.detailText}>{selected.text}</Typography>
              <Typography variant="body2" color="textSecondary">
                <b>Tipo:</b> {categoryLabel(selected.category)}
                <br />
                <b>Empresa:</b> {selected.company?.name || selected.companyId}
                <br />
                <b>Usuário:</b> {selected.user?.name || "—"} ({selected.user?.email || "—"})
                <br />
                <b>Tela:</b> {selected.pageUrl || "—"}
                <br />
                <b>Navegador:</b> {selected.userAgent || "—"}
                <br />
                <b>Enviada em:</b> {moment(selected.createdAt).format("DD/MM/YYYY HH:mm")}
              </Typography>
              {selected.mediaPath && (
                screenshotUrl ? (
                  <a href={screenshotUrl} target="_blank" rel="noopener noreferrer">
                    <img src={screenshotUrl} alt={selected.mediaName} className={classes.screenshot} />
                  </a>
                ) : (
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                    Carregando print...
                  </Typography>
                )
              )}
            </DialogContent>
            <DialogActions>
              <StatusSelect
                value={selected.status}
                onChange={(status) => handleStatusChange(selected, status)}
                className={classes.statusSelect}
              />
              <div style={{ flex: 1 }} />
              <Button onClick={handleCloseDetail} color="primary" variant="outlined">
                Fechar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <MainHeader>
        <Grid style={{ width: "99.6%" }} container alignItems="center">
          <Grid xs={12} sm={8} item>
            <Title>Sugestões de melhoria ({count})</Title>
          </Grid>
          <Grid xs={12} sm={4} item>
            <FormControl fullWidth margin="dense" variant="outlined">
              <InputLabel id="suggestion-status-filter">Status</InputLabel>
              <Select
                labelId="suggestion-status-filter"
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {SUGGESTION_STATUSES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Título</TableCell>
              <TableCell>Empresa</TableCell>
              <TableCell>Usuário</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((s) => (
              <TableRow key={s.id} hover>
                <TableCell>{moment(s.createdAt).format("DD/MM/YYYY HH:mm")}</TableCell>
                <TableCell>{categoryLabel(s.category)}</TableCell>
                <TableCell>
                  <div className={classes.title} title={s.title}>
                    {s.title}
                  </div>
                </TableCell>
                <TableCell>{s.company?.name || s.companyId}</TableCell>
                <TableCell>{s.user?.name || "—"}</TableCell>
                <TableCell align="center">
                  <StatusSelect
                    value={s.status}
                    onChange={(status) => handleStatusChange(s, status)}
                    className={classes.statusSelect}
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Ver detalhes">
                    <IconButton size="small" onClick={() => handleOpenDetail(s)}>
                      <VisibilityOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                  {s.mediaPath && (
                    <Tooltip title="Possui print">
                      <ImageOutlinedIcon fontSize="small" color="action" style={{ verticalAlign: "middle", marginLeft: 4 }} />
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={7} />}
            {!loading && records.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary" style={{ padding: 24 }}>
                    Nenhuma sugestão encontrada.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Suggestions;
